import React, { useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaMinus } from 'react-icons/fa';
import SectionHeader from '../common/SectionHeader';

const SyllabusContainer = styled.section`
  padding: 80px 0;
  scroll-margin-top: 100px; /* For smooth scroll to this section */
`;

const TabsContainer = styled.div`
  background-color: white;
  border-radius: ${props => props.theme.borders.radius.regular};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.regular};
`;

const TabButtons = styled.div`
  display: flex;
  background-color: ${props => props.theme.colors.secondaryColor};
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const TabButton = styled.button`
  flex: 1;
  padding: 15px;
  background-color: ${props => 
    props.active ? props.theme.colors.primaryColor : 'transparent'};
  border: none;
  color: white;
  font-weight: ${props => props.theme.fontWeights.semiBold};
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.regular};
  
  &:hover {
    background-color: ${props => 
      props.active ? props.theme.colors.primaryColor : 'rgba(255, 255, 255, 0.1)'};
  }
  
  &:focus {
    outline: none;
  }
`;

const TabContent = styled.div`
  padding: 30px;
`;

const TabPane = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const TabTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xlarge};
  margin-bottom: 30px;
  text-align: center;
  color: ${props => props.theme.colors.secondaryColor};
`;

const AccordionContainer = styled.div`
  margin-top: 30px;
`;

const AccordionItem = styled.div`
  margin-bottom: 15px;
  border-radius: ${props => props.theme.borders.radius.small};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
`;

const AccordionHeader = styled.div`
  background-color: ${props => props.theme.colors.lightColor};
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  
  &:hover {
    background-color: ${props => props.theme.colors.grey[200]};
  }
`;

const AccordionTitle = styled.h4`
  margin: 0;
  color: ${props => props.theme.colors.secondaryColor};
  font-size: ${props => props.theme.fontSizes.medium};
`;

const AccordionIcon = styled.div`
  color: ${props => props.theme.colors.primaryColor};
  font-size: 1rem;
`;

const AccordionContent = styled.div`
  padding: ${props => props.isOpen ? '20px' : '0 20px'};
  background-color: white;
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease;
`;

const TopicList = styled.ul`
  list-style-position: inside;
  color: ${props => props.theme.colors.grey[600]};
  
  li {
    margin-bottom: 10px;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SyllabusSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeAccordions, setActiveAccordions] = useState({});
  
  const toggleAccordion = (tabIndex, accordionIndex) => {
    setActiveAccordions(prev => {
      const key = `${tabIndex}-${accordionIndex}`;
      return {
        ...prev,
        [key]: !prev[key]
      };
    });
  };
  
  const isAccordionOpen = (tabIndex, accordionIndex) => {
    const key = `${tabIndex}-${accordionIndex}`;
    return !!activeAccordions[key];
  };
  
  const syllabusData = [
    {
      title: "Paper 1",
      sections: [
        {
          title: "General Science",
          topics: [
            "Physics - Nature of Universe, General Scientific Laws",
            "Chemistry - Physical and Chemical changes",
            "Botany - Main concepts of life science",
            "Zoology - Blood and blood circulation"
          ]
        },
        {
          title: "Current Events",
          topics: [
            "Latest National and International Events",
            "Current affairs - National and International",
            "Sports - Important Events",
            "Important Government announcements and policies"
          ]
        },
        {
          title: "Geography",
          topics: [
            "Earth and Universe - Solar system",
            "Atmosphere, hydrosphere, lithosphere",
            "Monsoon, rainfall, weather and climate",
            "Major natural resources"
          ]
        },
        {
          title: "History and Culture of India",
          topics: [
            "Indus valley civilization",
            "Vedic, Aryan and Sangam age",
            "Maurya dynasty",
            "Struggle for independence"
          ]
        }
      ]
    },
    {
      title: "Paper 2",
      sections: [
        {
          title: "Civil Engineering",
          topics: [
            "Building Materials - Physical and Chemical properties",
            "Estimating, Costing and Valuation",
            "Surveying - Principles and Classifications",
            "Soil Mechanics - Properties and Classifications"
          ]
        },
        {
          title: "Structural Engineering",
          topics: [
            "Theory of Structures",
            "Concrete Technology",
            "RCC Design",
            "Steel Design"
          ]
        },
        {
          title: "Building Construction",
          topics: [
            "Brick masonry",
            "Types of foundations",
            "Classification of buildings",
            "Building components and their functions"
          ]
        },
        {
          title: "Transportation Engineering",
          topics: [
            "Highway Planning",
            "Traffic Engineering",
            "Highway Materials",
            "Construction and Maintenance of Highways"
          ]
        }
      ]
    }
  ];

  return (
    <SyllabusContainer id="syllabus">
      <div className="container">
        <SectionHeader 
          title="Course Syllabus" 
          subtitle="Detailed curriculum for TNPSC Combined Technical Services Examination" 
        />
        
        <TabsContainer>
          <TabButtons>
            {syllabusData.map((tab, index) => (
              <TabButton 
                key={index} 
                active={activeTab === index}
                onClick={() => setActiveTab(index)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabButtons>
          
          <TabContent>
            {syllabusData.map((tab, tabIndex) => (
              <TabPane 
                key={tabIndex} 
                active={activeTab === tabIndex}
              >
                <TabTitle>{tab.title} - Syllabus</TabTitle>
                
                <AccordionContainer>
                  {tab.sections.map((section, accordionIndex) => (
                    <AccordionItem key={accordionIndex}>
                      <AccordionHeader 
                        onClick={() => toggleAccordion(tabIndex, accordionIndex)}
                      >
                        <AccordionTitle>{section.title}</AccordionTitle>
                        <AccordionIcon>
                          {isAccordionOpen(tabIndex, accordionIndex) ? <FaMinus /> : <FaPlus />}
                        </AccordionIcon>
                      </AccordionHeader>
                      <AccordionContent 
                        isOpen={isAccordionOpen(tabIndex, accordionIndex)}
                      >
                        <TopicList>
                          {section.topics.map((topic, topicIndex) => (
                            <li key={topicIndex}>{topic}</li>
                          ))}
                        </TopicList>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </AccordionContainer>
              </TabPane>
            ))}
          </TabContent>
        </TabsContainer>
      </div>
    </SyllabusContainer>
  );
};

export default SyllabusSection;